/**
 * CatalogDragDrop — GVW-123 Drag-and-drop layer add
 * 
 * Per brief.md: A full-window overlay that appears when the user starts dragging files 
 * into the workspace. Shows large icon + body + helper. On drop, a Toast surfaces the result.
 */

import { useState, useEffect, useCallback } from 'react';

interface CatalogDragDropProps {
  className?: string;
}

interface DragDropState {
  isDragActive: boolean;
  dragCounter: number;
}

export function CatalogDragDrop({ className }: CatalogDragDropProps): JSX.Element {
  const [state, setState] = useState<DragDropState>({
    isDragActive: false,
    dragCounter: 0,
  });

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    
    // Only show overlay for file drags
    if (e.dataTransfer?.types.includes('Files')) {
      setState(prev => ({
        dragCounter: prev.dragCounter + 1,
        isDragActive: true,
      }));
    }
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    
    setState(prev => {
      const newCounter = prev.dragCounter - 1;
      return {
        dragCounter: newCounter,
        isDragActive: newCounter > 0,
      };
    });
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    
    setState({
      isDragActive: false,
      dragCounter: 0,
    });

    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      
      // Process each file
      files.forEach((file) => {
        try {
          // In a real implementation, this would call useLayerStore.addLayerFromFile()
          console.log('Adding file as layer:', file.name);
          
          // Simulate success toast
          showToast(`Added ${file.name} as a layer.`, 'success');
        } catch {
          // Simulate error toast
          showToast(`We could not add ${file.name}. Open the converter for details.`, 'error');
        }
      });
    }
  }, []);

  const showToast = (message: string, type: 'success' | 'error') => {
    // In a real implementation, this would use a proper toast system
    console.log(`Toast (${type}):`, message);
    
    // Create a temporary toast element for demonstration
    const toast = document.createElement('div');
    toast.className = `drag-drop-toast drag-drop-toast--${type}`;
    toast.innerHTML = `
      <span class="material-icons" aria-hidden="true">
        ${type === 'success' ? 'check_circle' : 'error'}
      </span>
      ${message}
    `;
    toast.setAttribute('role', type === 'success' ? 'status' : 'alert');
    
    document.body.appendChild(toast);
    
    // Remove after 3 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 3000);
  };

  useEffect(() => {
    // Add global drag and drop listeners
    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);

    return () => {
      // Clean up listeners
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  // Don't render anything if not dragging
  if (!state.isDragActive) {
    return <></>;
  }

  return (
    <div 
      className={`catalog-drag-drop ${className || ''}`}
      role="status"
      aria-live="polite"
      data-testid="catalog-drag-drop"
    >
      <div className="catalog-drag-drop__overlay">
        <div className="catalog-drag-drop__content">
          <span className="material-icons catalog-drag-drop__icon" aria-hidden="true">
            cloud_upload
          </span>
          
          <h1 className="catalog-drag-drop__heading">
            Drop to add as a layer
          </h1>
          
          <p className="catalog-drag-drop__helper">
            You can also use Add layer for catalog items.
          </p>
        </div>
      </div>
    </div>
  );
}